import { Button } from 'module/ui/components/atoms/button';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { switchProp } from 'styled-tools';
import { prop } from 'styled-tools';

export interface InfoBoxProps {
  title: string | ReactNode;
  info: ReactNode;
  image?: ReactNode;
  actionButton?: {
    text: string;
    onClick: () => void;
    dataCy?: string;
  };
  layout?: 'row' | 'reverse';
  className?: string;
}

const InfoBoxContainer = styled.div<{ type: 'row' | 'reverse' }>`
  display: flex;
  justify-content: space-between;
  height: fit-content;
  margin: var(--spacing-06);
  background: var(--scarlet-gray-20);
  border-radius: var(--spacing-03);

  ${switchProp(prop('type'), {
    reverse: css`
      flex-direction: row-reverse;
    `,
    row: css`
      flex-direction: row;
    `,
  })};
`;

const InfoBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoBoxTextItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 100%;
  height: 100%;

  svg {
    width: 20%;
    margin: 0 var(--spacing-03) 0 0;
  }
`;

const InfoBoxImageTextItem = styled.div`
  gap: var(--spacing-05);
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  text-align: start;
  padding: var(--spacing-06);
`;

const InfoBoxTextTitle = styled.div`
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  height: var(--heading-4-size);
  align-items: center;
  display: flex;
`;

const BannerWrapper = styled.div`
  padding: var(--spacing-03) 0 var(--spacing-06) 0;
  display: flex;
  flex-direction: column;
  align-items: end;
  height: fit-content;
  width: 100%;
  text-align: right;
  color: var(--gray-60);
  font-size: var(--font-size-sm);

  button {
    padding: var(--spacing-02) var(--spacing-04);
    color: var(--white-100);
    font-size: var(--font-size-base);

    &:hover {
      text-decoration: none;
      border-radius: 0;
    }
  }
`;

const InfoBoxAction = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: var(--spacing-04);
  gap: var(--spacing-09);
`;

const ActionButton = styled(Button)`
  background: var(--button-primary);
  border: 0 !important;
  color: var(--white-100);

  p {
    flex: 1;
    text-align: left;
  }

  &:hover,
  &:focus {
    background: var(--button-primary);
    color: var(--white-100) !important;
    text-decoration: none !important;
    border-radius: 0;
  }
`;

export const InfoBox: React.FC<InfoBoxProps> = ({
  title,
  info,
  image,
  actionButton,
  layout = 'row',
  className,
}) => {
  const renderTitle = () => {
    if (typeof title === 'string') {
      return <FormattedMessage id={title} />;
    }

    return title;
  };

  const renderActionButton = () => {
    if (!actionButton) {
      return null;
    }

    return (
      <InfoBoxAction>
        <ActionButton
          type="link"
          onClick={actionButton.onClick}
          data-cy={actionButton.dataCy}
        >
          <FormattedMessage id={actionButton.text} />
        </ActionButton>
      </InfoBoxAction>
    );
  };

  return (
    <InfoBoxContainer type={layout} className={className}>
      {image && <BannerWrapper>{image}</BannerWrapper>}
      <InfoBoxWrapper>
        <InfoBoxTextItemWrapper>
          <InfoBoxImageTextItem>
            <InfoBoxTextTitle>{renderTitle()}</InfoBoxTextTitle>
            {info}
            {renderActionButton()}
          </InfoBoxImageTextItem>
        </InfoBoxTextItemWrapper>
      </InfoBoxWrapper>
    </InfoBoxContainer>
  );
};
